import React, { useMemo } from "react";
import { Button, Space, Table, Tag } from "antd";
import { formatSearchDate } from "../search/searchResultUtils";
import { SORT_BY, withServerSorter, sorterToQuery } from "../../utils/tableSortParams";
import { SUBMIT_CLAIM_LABEL, canCheckoutFor, canSubmitClaimFor } from "./claimSubmission";

const CHECKIN_TYPE_MAP = { 1: "In-Patient", 2: "Out-Patient" };
const CHECKIN_SOURCE_MAP = { 0: "Facility", 1: "Manual PA" };
const STATUS_MAP = { 0: "Active", 1: "Closed" };
const TYPE_COLOR_MAP = { 1: "magenta", 2: "geekblue" };
const SOURCE_COLOR_MAP = { 1: "gold", 0: "cyan" };
const STATUS_COLOR_MAP = { 0: "green", 1: "default" };

function patientName(record) {
  const full = `${record?.surname?.toUpperCase?.() || ""}${record?.surname ? "," : ""} ${record?.first_name || ""} ${record?.middle_name || ""}`.trim();
  return full || record?.patient_name || "—";
}

function formatNaira(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₦${n.toLocaleString()}`;
}

export default function EncountersQueueTable({
  data,
  userRoleId,
  onOpenDetails,
  onViewPA,
  onCheckout,
  isCheckoutLoading,
  sortBy,
  sortDir,
  onSortChange,
  pmUnmapped,
  // New flow (spec §7): Submit Claim replaces Checkout, and rows can be
  // selected for bulk submission. Off = today's table, unchanged.
  submitMode = false,
  selectedRowKeys,
  onSelectionChange,
}) {
  const sort = { sort_by: sortBy, sort_dir: sortDir };
  const columns = useMemo(() => {
    const canViewPA = (record) => record?.code && [1, 2, 3].includes(userRoleId);
    const canCheckout = (record) =>
      submitMode ? canSubmitClaimFor(record, userRoleId) : canCheckoutFor(record, userRoleId);

    return [
      {
        title: "Patient",
        key: "patient",
        ellipsis: true,
        fixed: "left",
        width: 160,
        render: (_, record) => (
          <div className="encounters-table__patient">
            <span className="encounters-table__patient-name">{patientName(record)}</span>
            <span className="encounters-table__patient-meta">{record?.insurance_no || "—"}</span>
          </div>
        ),
      },
      {
        title: "Encounter",
        dataIndex: "code",
        key: "code",
        width: 160,
        ellipsis: true,
        render: (v) => {
          const unmapped = pmUnmapped ? Number(pmUnmapped[v] || 0) : 0;
          return (
            <span>
              {v || "—"}
              {unmapped > 0 ? (
                <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: '16px', paddingInline: 4 }}>
                  {unmapped} unmapped
                </Tag>
              ) : null}
            </span>
          );
        },
      },
      withServerSorter(
        {
          title: "Check-in",
          key: SORT_BY.CHECKIN_DATE,
          width: 130,
          render: (_, record) => formatSearchDate(record?.checkin_date),
        },
        SORT_BY.CHECKIN_DATE,
        sort
      ),
      {
        title: "Facility",
        dataIndex: "facility_name",
        key: "facility_name",
        ellipsis: true,
        width: 160,
      },
      {
        title: "Type",
        key: "type",
        width: 110,
        responsive: ["md"],
        render: (_, record) => {
          const typeKey = Number(record?.checkinType);
          const label = record?.checkin_type_name || CHECKIN_TYPE_MAP[typeKey] || "—";
          return <Tag color={TYPE_COLOR_MAP[typeKey] || "geekblue"}>{label}</Tag>;
        },
      },
      withServerSorter(
        {
          title: "Items",
          key: SORT_BY.ITEMS,
          width: 72,
          align: "right",
          render: (_, record) => (record?.pa_items_count != null && record.pa_items_count !== "" ? record.pa_items_count : "—"),
        },
        SORT_BY.ITEMS,
        sort
      ),
      withServerSorter(
        {
          title: "Cost",
          key: SORT_BY.AMOUNT,
          width: 110,
          align: "right",
          responsive: ["lg"],
          render: (_, record) => formatNaira(record?.requested_total),
        },
        SORT_BY.AMOUNT,
        sort
      ),
      {
        title: "Source",
        key: "source",
        width: 100,
        responsive: ["lg"],
        render: (_, record) => {
          const sourceKey = Number(record?.source);
          const label = CHECKIN_SOURCE_MAP[sourceKey] || "—";
          return <Tag color={SOURCE_COLOR_MAP[sourceKey] || "cyan"}>{label}</Tag>;
        },
      },
      {
        title: "Status",
        key: "status",
        width: 88,
        render: (_, record) => {
          const statusKey = Number(record?.checkin_status) === 0 ? 0 : 1;
          return (
            <Space size={2} wrap>
              <Tag color={STATUS_COLOR_MAP[statusKey] || "default"}>
                {submitMode && record?.auto_checked_out_at ? "Auto closed" : STATUS_MAP[statusKey] || "Unknown"}
              </Tag>
              {submitMode && record?.pending_submission ? <Tag color="gold">Claim pending</Tag> : null}
            </Space>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 148,
        fixed: "right",
        render: (_, record) => (
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            {canViewPA(record) ? (
              <Button type="link" size="small" className="encounters-table__action" onClick={() => onViewPA?.(record)}>
                Pre-auth
              </Button>
            ) : null}
            {canCheckout(record) ? (
              <Button
                type="link"
                size="small"
                className="encounters-table__action"
                loading={isCheckoutLoading}
                onClick={() => onCheckout?.(record)}
              >
                {submitMode ? SUBMIT_CLAIM_LABEL : "Checkout"}
              </Button>
            ) : null}
          </Space>
        ),
      },
    ];
  }, [userRoleId, onViewPA, onCheckout, isCheckoutLoading, sort.sort_by, sort.sort_dir, pmUnmapped, submitMode]);

  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <Table
      className="search-results-table encounters-queue-table"
      size="small"
      rowKey={(record) => record.code || record.insurance_no}
      columns={columns}
      dataSource={data}
      scroll={{ x: "max-content" }}
      pagination={false}
      rowSelection={
        submitMode && onSelectionChange
          ? {
              selectedRowKeys: selectedRowKeys || [],
              onChange: (keys) => onSelectionChange(keys),
              getCheckboxProps: (record) => ({
                disabled: !canSubmitClaimFor(record, userRoleId),
              }),
              preserveSelectedRowKeys: true,
            }
          : undefined
      }
      onChange={(_pagination, _filters, sorter) => {
        const next = sorterToQuery(sorter);
        if (next.sort_by) onSortChange?.(next);
      }}
      onRow={(record) => ({
        onClick: () => onOpenDetails?.(record),
        style: { cursor: "pointer" },
      })}
    />
  );
}
